import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import api from '@/lib/api';
import { asPlainText, shouldShowBonitaFeedPost } from '@/lib/safeText';
import { fetchStoryPosts } from '@/lib/storyPosts';

// Time ago helper
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = (now - date) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ── Bonita Widget ──
const BonitaWidget = ({ bonitaPost, stats }) => (
  <div className="sw bonita-w">
    <div className="sw-hdr">&#10024; Bonita Is Hosting</div>
    {bonitaPost ? (
      <>
        <div className="bw-msg">
          &ldquo;{(() => {
            const t = asPlainText(bonitaPost.content);
            return t.length > 200 ? `${t.slice(0, 200)}...` : t;
          })()}&rdquo;
        </div>
        <div className="bw-sig">&mdash; Bonita &middot; {timeAgo(bonitaPost.created_at)}</div>
      </>
    ) : (
      <div className="bw-msg">
        &ldquo;I’m keeping the room warm. Drop a take, check the calendar, or let me spark the next conversation.&rdquo;
      </div>
    )}
    <div className="bw-sig">
      {stats?.active_today ? `${stats.active_today} neighbors pulled up today` : 'Bonita keeps the sidewalk warm when traffic is light'}
    </div>
    <div className="bw-btns">
      <Link to="/bonita?mode=vibe" className="bw-btn">Vibe Check</Link>
      <Link to="/bonita?mode=tone" className="bw-btn">Tone Lab</Link>
      <Link to="/bonita" className="bw-btn">Ask Bonita</Link>
    </div>
  </div>
);

function stoopHostDisplayLabel(host) {
  if (host == null || host === '') return 'admin';
  if (typeof host === 'object') {
    return host.username || host.name || 'admin';
  }
  return String(host);
}

// ── Stoop Live Widget ──
const StoopLiveWidget = ({ room }) => (
  <div className="sw stoop-w">
    <div className="sw-hdr">&#127897; The Stoop</div>
    {room ? (
      <div className="stw-room">
        <div className="stw-title">{room.title || 'Live Session'}</div>
        <div className="stw-host">hosted by @{stoopHostDisplayLabel(room.host)}</div>
        <div className="stw-meta">
          <span className="stw-live">
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#18B574', display: 'inline-block', animation: 'livePip 1.8s ease-in-out infinite' }} />
            LIVE
          </span>
          <span>{room.listeners || 0} listening</span>
        </div>
        <Link to="/stoop" className="stw-btn">Pull Up &#8594;</Link>
      </div>
    ) : (
      <>
        <div className="stw-empty">No active sessions right now</div>
        <Link to="/stoop" className="stw-btn">Visit The Stoop &#8594;</Link>
      </>
    )}
  </div>
);

// ── Pulse Widget ──
const PulseWidget = ({ stats }) => (
  <div className="sw pulse-w">
    <div className="sw-hdr">&#9889; The Pulse</div>
    <div className="pr">
      <span className="pl">On The Block</span>
      <span className="pv live">{stats?.active_today ?? '--'} today</span>
    </div>
    <div className="pr">
      <span className="pl">Posts Dropped</span>
      <span className="pv hot">{stats?.posts_today ?? '--'}</span>
    </div>
    <div className="pr">
      <span className="pl">Community</span>
      <span className="pv">{stats?.total_users ?? '--'} members</span>
    </div>
  </div>
);

// ── Who's Here Widget ──
const WhosHereWidget = ({ users }) => (
  <div className="sw whos-w">
    <div className="sw-hdr">&#128065; Who's Here</div>
    {users.length === 0 ? (
      <div style={{ fontSize: 11, color: '#3A3F4E', fontStyle: 'italic' }}>The Block is quiet...</div>
    ) : (
      users.slice(0, 5).map(u => (
        <Link to={`/profile/${u.username}`} className="wr" key={u.user_id}>
          <Avatar className="wr-av h-6 w-6">
            <AvatarImage src={u.picture} alt={u.name} />
            <AvatarFallback style={{ fontSize: 9 }}>{(u.name || u.username || 'U').charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <span className="wr-name">@{u.username}</span>
          <span className={`wr-dot ${u.activity_status === 'recently' ? 'away' : ''}`} />
        </Link>
      ))
    )}
  </div>
);

// ── HBCU.news Widget ──
const HBCUNewsWidget = ({ stories }) => (
  <div className="sw news-w">
    <div className="sw-hdr">&#128240; HBCU.news</div>
    {stories.length === 0 ? (
      <div className="stw-empty">Fresh campus stories will land here as soon as they hit the Block.</div>
    ) : (
      stories.map((story) => {
        const title =
          asPlainText(story.metadata?.headline) ||
          asPlainText(story.content).slice(0, 90) ||
          'From the Yard';
        const cat = asPlainText(story.metadata?.category).toUpperCase();
        const school = asPlainText(story.metadata?.school);
        return (
          <Link to={`/post/${story.post_id}`} className="nw-item block" key={story.post_id}>
            {cat && <div className="nw-cat">{cat}</div>}
            <div className="nw-title">{title}</div>
            <div className="nw-src">{school || 'hbcu.news'} &middot; {timeAgo(story.created_at)}</div>
          </Link>
        );
      })
    )}
    <Link to="/word" className="nw-all">
      All Stories &#8594;
    </Link>
  </div>
);

// ── Main FeedSidebar ──
export const FeedSidebar = ({ className }) => {
  const [bonitaPost, setBonitaPost] = useState(null);
  const [stats, setStats] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [stoopRoom, setStoopRoom] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      const [postRes, statsRes, onlineRes, stoopRes, storiesRes] = await Promise.allSettled([
        api.get('/posts/user/bonita?limit=15', { withCredentials: true }),
        api.get('/stats/pulse', { withCredentials: true }),
        api.get('/users/online', { params: { limit: 5 }, withCredentials: true }),
        api.get('/stoop/live', { withCredentials: true }),
        fetchStoryPosts({ limit: 3 }),
      ]);

      if (postRes.status === 'fulfilled') {
        const posts = postRes.value.data;
        if (Array.isArray(posts)) {
          const good = posts.find((p) => shouldShowBonitaFeedPost(p));
          if (good) setBonitaPost(good);
        }
      }
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (onlineRes.status === 'fulfilled') setOnlineUsers(onlineRes.value.data?.users || []);
      if (stoopRes.status === 'fulfilled') {
        const data = stoopRes.value.data;
        const rooms = Array.isArray(data) ? data : (data?.rooms ?? []);
        setStoopRoom(rooms[0] ?? null);
      }
      if (storiesRes.status === 'fulfilled') {
        setStories(Array.isArray(storiesRes.value) ? storiesRes.value : []);
      }

      setLoading(false);
    };

    fetchAll();
    const interval = setInterval(fetchAll, 120000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className={`sidebar-widgets ${className || ''}`}>
        <Skeleton className="h-4 w-24 mb-4" />
        <Skeleton className="h-20 w-full mb-6" />
        <Skeleton className="h-4 w-20 mb-3" />
        <Skeleton className="h-16 w-full mb-6" />
        <Skeleton className="h-4 w-20 mb-3" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <div className={`sidebar-widgets ${className || ''}`}>
      <BonitaWidget bonitaPost={bonitaPost} stats={stats} />
      <StoopLiveWidget room={stoopRoom} />
      <PulseWidget stats={stats} />
      <WhosHereWidget users={onlineUsers} />
      <HBCUNewsWidget stories={stories} />
    </div>
  );
};
